/**
 * a3pDecode.js — read a GAM `a3p` (Secure Signals) value into its adapter entries,
 * so the sign-off report can show what each SDK-bidding buyer sent instead of a
 * 2,000-char blob.
 *
 * Wire shape (Android GMA, 19 Oct 2026 capture): base64url of a protobuf with one
 * repeated field 1 per adapter: { 2: SDK version a.b.c, 3: adapter version a.b.c,
 * 4: the adapter's signal string, 5: adapter class name }. Liftoff's signal is
 * "<n>:<base64 gzip of a JSON bid token>"; UID2 / LiveRamp send an empty signal.
 *
 * Sync and never throws: gzip needs DecompressionStream (async), so a gzipped
 * signal is returned as base64 for the report page to inflate when it opens.
 */

const BUYERS = [
  [/vungle|liftoff/i, 'Liftoff (Vungle)'],
  [/facebook|\bmeta\b/i, 'Meta'],
  [/applovin/i, 'AppLovin'],
  [/mintegral/i, 'Mintegral'],
  [/pangle|bytedance/i, 'Pangle'],
  [/unity/i, 'Unity'],
  [/inmobi/i, 'InMobi'],
  [/fyber|inneractive|dtexchange/i, 'DT Exchange'],
  [/moloco/i, 'Moloco'],
  [/uid2/i, 'UID2'],
  [/liveramp|LRAts/i, 'LiveRamp ATS'],
];

export function a3pBuyerLabel(adapter = '') {
  const hit = BUYERS.find(([re]) => re.test(adapter));
  return hit ? hit[1] : String(adapter).split('.').pop();
}

function bytesFromBase64(s) {
  let t = String(s || '').trim();
  try { t = decodeURIComponent(t); } catch { /* not url-encoded */ }
  // GMA pads with '.' instead of '=' (iOS and Android a3p, 20-21 Oct 2026 captures).
  t = t.replace(/-/g, '+').replace(/_/g, '/').replace(/\./g, '=').replace(/\s/g, '');
  if (t.length % 4) t += '='.repeat(4 - (t.length % 4));
  const bin = atob(t);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function varint(b, i) {
  let x = 0; let s = 0; let c;
  do {
    if (i >= b.length) throw new Error('truncated varint');
    c = b[i]; i += 1;
    x += (c & 0x7f) * 2 ** s; s += 7;
  } while (c & 0x80);
  return [x, i];
}

/** Top-level protobuf fields: [{ field, wire, value (number) | bytes (Uint8Array) }]. */
function fields(b) {
  const out = [];
  let i = 0;
  while (i < b.length) {
    let tag; [tag, i] = varint(b, i);
    const field = Math.floor(tag / 8); const wire = tag & 7;
    if (wire === 0) { let v; [v, i] = varint(b, i); out.push({ field, wire, value: v }); }
    else if (wire === 2) {
      let len; [len, i] = varint(b, i);
      if (i + len > b.length) throw new Error('truncated field');
      out.push({ field, wire, bytes: b.subarray(i, i + len) }); i += len;
    } else if (wire === 5) i += 4;
    else if (wire === 1) i += 8;
    else throw new Error(`wire type ${wire}`);
  }
  return out;
}

const text = (bytes) => Array.from(bytes || [], (c) => String.fromCharCode(c)).join('');
const version = (bytes) => {
  try {
    const v = fields(bytes).filter((f) => f.wire === 0).map((f) => f.value);
    return v.length && v.some(Boolean) ? v.join('.') : null;
  } catch { return null; }
};

/**
 * @returns {{ ok: boolean, bytes: number, entries: Array<{ adapter, buyer, sdkVersion,
 *   adapterVersion, signalLength, signal, gzipBase64 }>, error?: string }}
 */
export function decodeA3p(raw) {
  if (!raw) return { ok: false, bytes: 0, entries: [], error: 'no a3p on this request' };
  let b;
  try { b = bytesFromBase64(raw); } catch { return { ok: false, bytes: 0, entries: [], error: 'a3p is not base64' }; }
  try {
    const entries = fields(b).filter((f) => f.field === 1 && f.bytes).map((f) => {
      const sub = fields(f.bytes);
      const get = (n) => sub.find((x) => x.field === n && x.bytes);
      const adapter = text(get(5)?.bytes);
      const signal = text(get(4)?.bytes);
      const gz = signal.match(/^\d+:(H4sI[A-Za-z0-9+/_=-]+)$/);
      return {
        adapter,
        buyer: a3pBuyerLabel(adapter),
        sdkVersion: version(get(2)?.bytes),
        adapterVersion: version(get(3)?.bytes),
        signalLength: signal.length,
        signal: gz ? null : signal,
        gzipBase64: gz ? gz[1] : null,
      };
    }).filter((e) => e.adapter);
    return { ok: entries.length > 0, bytes: b.length, entries, ...(entries.length ? {} : { error: 'no adapter entries found' }) };
  } catch (e) {
    return { ok: false, bytes: b.length, entries: [], error: `a3p did not parse (${e.message})` };
  }
}
